import classNames from 'classnames';
import Image from 'next/image';
import React, {ReactElement, useCallback, useMemo, useState} from 'react';

import Col from '../../../common/Col';
import Row from '../../../common/Row';
import {Product} from '../../../query/graphql';
import {formatDate} from '../../decision/util';
import {SupplierBasic} from '../../marketplace/types';
import CircleImage from '../CircleImage';
import ProductDetailModal from './ProductDetailModal';

type Props = {
  products: Product[];
  // If provided, will show the supplier name for each product
  approvedSuppliers?: SupplierBasic[];
  title?: string;
  className?: string;
};

const SupplierProducts = ({products, approvedSuppliers, title, className}: Props): ReactElement => {
  const [openProduct, setOpenProduct] = useState<Product>();
  const [openProductSupplier, setOpenProductSupplier] = useState<SupplierBasic>();

  const supplierMap = useMemo(
    () => (!approvedSuppliers ? undefined : Object.fromEntries(approvedSuppliers.map(s => [s.id, s]))),
    [approvedSuppliers]
  );

  const closeProductDetail = useCallback(() => setOpenProduct(undefined), []);

  return (
    <Col className={classNames('gap-sm', className)}>
      <ProductDetailModal product={openProduct} supplier={openProductSupplier} onRequestClose={closeProductDetail} />
      {title && <h2 className="text-2xl font-semibold">{title}</h2>}
      {products.length ? (
        <div className={'grid grid-cols-3 gap-md tablet:grid-cols-1 px-[2px]'}>
          {products.map((product, idx) => {
            const supplierName = supplierMap && product.supplierId ? supplierMap[product.supplierId]?.name : undefined;
            return (
              <button
                key={idx}
                className={'relative flex flex-col gap-2 text-left group overflow-hidden'}
                onClick={() => {
                  setOpenProduct(product);
                  if (product.supplierId) setOpenProductSupplier(supplierMap?.[product.supplierId]);
                }}
              >
                <Row
                  className={classNames(
                    'relative w-full h-[200px] border-[1px] border-gray-100 rounded-3xl overflow-hidden items-center justify-center',
                    'group-hover:border-primary-300'
                  )}
                >
                  {product.image ? (
                    <Image src={product.image.url} alt={product.name} fill className={'object-cover'} />
                  ) : (
                    <CircleImage size={'large'} src={null} alt={product.name} />
                  )}
                </Row>
                {!!product.promoted_until && (
                  <div
                    className={classNames(
                      'absolute top-0 left-0 right-0',
                      'border-y-thin border-primary-300 py-2 pl-[4rem] bg-primary-600',
                      'text-[12px] text-white font-semibold leading-tight',
                      'origin-bottom-left -rotate-45 translate-y-[5rem]'
                    )}
                  >
                    <div className={'w-fit text-center'}>
                      Limited
                      <br />
                      time offer
                    </div>
                  </div>
                )}
                {!product.published && (
                  <div
                    className={classNames(
                      'absolute top-4 right-4',
                      'border-thin border-gray-100 rounded-full pl-3 pr-4 py-2 bg-white text-sm text-primary-700'
                    )}
                  >
                    <Row className={'items-center gap-2'}>
                      <div
                        className={
                          'flex-none w-[1.5rem] h-[1.5rem] rounded-full border-thin border-primary-300 bg-primary-100'
                        }
                      />
                      Draft
                    </Row>
                  </div>
                )}
                <Col className={'mt-xs gap-2 max-w-full'}>
                  {!!supplierName && <p className={'font-semibold text-sm text-primary-600'}>{supplierName}</p>}
                  <p className={'font-semibold leading-none group-hover:text-primary-500'}>{product.name}</p>
                  <p className={'text-sm line-clamp-4'}>{product.description}</p>
                  {product.promoted_until && (
                    <p className={'text-sm font-semibold'}>Deal ends on {formatDate(product.promoted_until)}</p>
                  )}
                </Col>
              </button>
            );
          })}
        </div>
      ) : (
        <p className={'text-sm text-gray-200'}>No products available</p>
      )}
    </Col>
  );
};

export default SupplierProducts;
